import {GeocentricCoordinate} from '../projections';
//import {parseFileChunks} from "../pointcloud";
import {readBufferChunks} from "../file-system-loader";
import {Projection} from '../../../redux/projectionsSlice';

const bytesPerMegabyte = 1048576;
const bytesPerChunk = 10 * bytesPerMegabyte;
const chunksToRead = 20;
const validDataFormats = [0,1,2,3,4,5,6,7,8,9,10];

const getCombinedBuffer = (bufferA, bufferB) => {
  let lengthA = bufferA.byteLength;
  let lengthB = bufferB.byteLength;
  let combined = new Uint8Array(lengthA + lengthB);
  combined.set(new Uint8Array(bufferA));
  combined.set(new Uint8Array(bufferB), bufferA.byteLength);
  return combined.buffer;
};

const readAs = (buf, Type, offset, count = 1) => {
  count = count === 0 ? 1 : count;
  let sub = buf.slice(offset, offset + Type.BYTES_PER_ELEMENT * count);

  let r = new Type(sub);
  if (count === undefined || count === 1) {
    return r[0];
  }

  let ret = [];
  for (var i = 0; i < count; i++) {
    ret.push(r[i]);
  }

  return ret;
};

const formatBounds = (b) => {
  return `[${b[0].toFixed(2)}, ${b[1].toFixed(2)}, ${b[2].toFixed(2)}]`;
};

const parseLASHeader = (arraybuffer, version, numBytes) => {
  let header: any = {};

  header.pointsOffset = readAs(arraybuffer, Uint32Array, 32 * 3);
  header.pointsFormatId = readAs(arraybuffer, Uint8Array, 32 * 3 + 8);
  header.pointsStructSize = readAs(arraybuffer, Uint16Array, 32 * 3 + 8 + 1);
  header.pointsCount = readAs(arraybuffer, Uint32Array, 32 * 3 + 11);
  header.latlong = false;
  header.utmzone = null;

  let start = 32 * 3 + 35;

  // Read the scale/offset
  header.scale = readAs(arraybuffer, Float64Array, start, 3);
  start += 24; // 8*3
  header.offset = readAs(arraybuffer, Float64Array, start, 3);
  start += 24;

  // Read the boundary
  let bounds = readAs(arraybuffer, Float64Array, start, 6);
  start += 48; // 8*6;
  header.maxs = [bounds[0], bounds[2], bounds[4]];
  header.mins = [bounds[1], bounds[3], bounds[5]];

  // Las 1.4 has 2 "pointscount" fields. Its hard to know which one
  // they filled out, so we will take the max value from the two
  if (version >= 14) {
    start += 20;
    let pointsCountLas14 = readUInt64(arraybuffer, start);
    header.pointsCount = Math.max(header.pointsCount, pointsCountLas14);
  }

  // Check for files with over 4 billion points (allow for lots of evlrs data when
  // calculating our approximate points)
  let approxPointCount = (numBytes - header.pointsOffset) / header.pointsStructSize;
  let pointDifference = Math.abs(approxPointCount - header.pointsCount);
  if (pointDifference > Math.pow(2, 31)) {
    header.pointsCount += Math.pow(2, 32);
  }

  let warnings = [];
  let maxBoundaryDifference = 1000 * 1000; // >1000km in any direction

  // Boundary issues: Warn the user and tell them to fix it with the proper function
  let diffs = [
    Math.abs(header.maxs[0] - header.mins[0]),
    Math.abs(header.maxs[1] - header.mins[1]),
    Math.abs(header.maxs[2] - header.mins[2])
  ];
  let maxDiff =  Math.max(Math.max(diffs[0], diffs[1]), diffs[2]);

  let largeBoundary = maxDiff > maxBoundaryDifference;
  let emptyBoundary = maxDiff === 0;
  let longLatData = isLongLatData(header, diffs);

  if (largeBoundary) {
    // Detect really large boundary
    warnings.push({
      messages: [
        "Large boundary detected in header data",
        `Header extent: ${formatBounds(diffs)}`,
      ],
      solution: 'Running the "Boundary Fix" function',
      issues: [
        "Data might not be visible",
        "Various visual bugs",
        "Not possible to display coordinates"
      ]
    });
  } else if (emptyBoundary) {
    // Detected empty boundary
    warnings.push({
      messages: [
        "Empty boundary detected in header data",
        `Header extent: ${formatBounds(diffs)}`,
      ],
      solution: 'Running the "Boundary Fix" function'
    });
  } else if (longLatData) {
    // Check for longlat data (and update the header values if detected)
    header.latlong = true;
    let centerPoint = (header.maxs[0] + header.mins[0]) / 2.0;
    let utmzone = (Math.floor((centerPoint + 180) / 6) % 60) + 1;

    header.latlongInfo = {
      utmzone: utmzone,
      offset: header.offset,
      max: header.maxs,
      min: header.mins,
      diff: [
        header.maxs[0] - header.mins[0],
        header.maxs[1] - header.mins[1]
      ]
    };

    let utmProjection = {
      type: "proj4",
      name: "Closest UTM",
      string: `+proj=utm +zone=${utmzone} +ellps=WGS84 +datum=NAD83 +no_defs`
    } as Projection;

    let newMin = new GeocentricCoordinate().fromLonLat(header.mins)
      .toProjection(utmProjection);
    newMin.z = header.mins[2];

    let newMax = new GeocentricCoordinate().fromLonLat(header.maxs)
      .toProjection(utmProjection);
    newMax.z = header.maxs[2];

    let newOffset = new GeocentricCoordinate().fromLonLat(header.offset)
      .toProjection(utmProjection);
    newOffset.z = header.offset[2];

    header.mins = newMin.toArray();
    header.maxs = newMax.toArray();
    header.offset = newOffset.toArray();

    warnings.push({
      messages: [
        "Latitude + longitude data detected",
        "A visual approximation will be used for viewing the data"
      ],
      solution: 'Running  "Projection Change" will allow you to use the data normally',
      issues: [
        "Functions like ground/noise will not work properly",
        "Data may appear slightly distorted over larger areas",
        "Measurements accuracy is reduced"
      ]
    });
  }

  // Calculate our center value (using original or utm-converted values)
  header.mean = [
    header.mins[0] + (header.maxs[0] - header.mins[0]) / 2.0,
    header.mins[1] + (header.maxs[1] - header.mins[1]) / 2.0,
    header.mins[2] + (header.maxs[2] - header.mins[2]) / 2.0
  ];

  header.size = [
    header.maxs[0] - header.mins[0],
    header.maxs[1] - header.mins[1],
    header.maxs[2] - header.mins[2]
  ];

  return {header, warnings};
};

const headerAsMeters = (header, toMeters) => {
  let attributes = ["maxs", "mean", "mins", "offset", "scale", "size"];
  attributes.forEach(name => {
    header[name][0] *= toMeters;
    header[name][1] *= toMeters;
    header[name][2] *= toMeters;
  });

  return header;
};

const readUInt64 = (buf, start) => {
  let arr = readAs(buf, Uint32Array, start, 2);
  return parseInt(arr[1] * Math.pow(2, 32) + arr[0]);
};

const isLongLatData = (header, diffs) => {
  let maxDegreeDifference = 2; // ~100km per degree
  if ((header.maxs[0] < 180) && (header.mins[0] > -180)) {
    if ((header.maxs[1] < 90) && (header.mins[1] > -90)) {
      if ((diffs[0] < maxDegreeDifference) && (diffs[1] < maxDegreeDifference)) {
        return true;
      }
    }
  }
  return false;
};

// LAS Loader
class LASLoader {
  private arraybuffers: any = [];
  private bufferArrayIndex = 0;
  private file: any;
  private numBytes: any;
  private version: any;
  private pointsRead: any;
  private header: any;
  private headerBytes: any;

  constructor(headerBytes, file, version, numBytes) {
    this.headerBytes = headerBytes;
    this.file = file;
    this.numBytes = numBytes;
    this.version = version;
  }

  get isRemoteFile() {
    return this.file.remote;
  }

  get pointsStructSize() {
    return this.header.pointsStructSize;
  }

  open() {
    this.pointsRead = 0;
    return new Promise(function (res, rej) {
      setTimeout(res, 0);
    });
  }

  getHeader(toMeters) {
    return new Promise((res, rej) => {
      setTimeout(() => {
        // Grab header from header bytes
        let {header, warnings} = parseLASHeader(this.headerBytes,
          this.version, this.numBytes);

        // Convert header values to meters
        header = headerAsMeters(header, toMeters);
        this.header = header;

        res({header: this.header, warnings: warnings});
      }, 0);
    });
  }

  readData() {
    return new Promise((res, rej) => {
      if (!this.header) {
        return rej(new Error("Cannot start reading data until a header request is issued"));
      }

      try {
        let results = this.getDataNoSkips();
        res(results);
      } catch (e) {
        console.log(e);
      }
    });
  }

  getDataNoSkips() {
    let buffer = null;
    let pointSize = this.header.pointsStructSize;

    if (this.bufferArrayIndex === (this.arraybuffers.length - 1)) {
      // Only one bufferarray left, so we grab the whole thing
      buffer = this.arraybuffers[this.bufferArrayIndex].slice(0);
    } else {
      // Grab from first array, and append extra to second array
      let bytesInChunk = this.arraybuffers[this.bufferArrayIndex].byteLength;
      let pointsInChunk = Math.floor(bytesInChunk / pointSize);
      let endByte = pointsInChunk * pointSize;
      buffer = this.arraybuffers[this.bufferArrayIndex].slice(0, endByte);
      let bufferExtraData = this.arraybuffers[this.bufferArrayIndex].slice(endByte);
      let nextBuffer = this.arraybuffers[this.bufferArrayIndex + 1].slice(0);
      this.arraybuffers[this.bufferArrayIndex + 1] = getCombinedBuffer(bufferExtraData, nextBuffer);
    }

    let count = buffer.byteLength / pointSize;
    this.arraybuffers[this.bufferArrayIndex] = null;
    this.bufferArrayIndex += 1;
    this.pointsRead += count;

    return {
      buffer: buffer,
      count: count,
      hasMoreData: this.pointsRead < this.header.pointsCount
    };
  }

  /*getSampledData(data, skips) {
    let writeIndex = 0;
    let pointSize = this.header.pointsStructSize;

    let buffer = new Uint8Array(data.buffer.slice(0));
    let sampled = new Uint8Array(buffer.byteLength);

    for (var i=0; i<data.count; i=i+skips) {
      let index = Math.floor(i) * pointSize;
      for (var j=0; j<pointSize; j++) {
        // @ts-ignore: Unreachable code error
        sampled[writeIndex + j] = buffer[index+j];
      }
      writeIndex += pointSize;
    }

    let newBuffer = sampled.slice(0, writeIndex).buffer;
    let newCount = newBuffer.byteLength / pointSize;

    console.warn("Point sampling should be webworker");
    console.log(`Sampled: ${data.count} --> ${newCount}`)

    // TODO: should we do the sampling in the cloud conversion instead? This
    // way we only need 1 webworker that will do everything for us

    return {
      buffer: newBuffer,
      count: newCount,
      hasMoreData: data.hasMoreData
    };
  }*/

  close() {
    return new Promise((res, rej) => {
      this.headerBytes = null;
      setTimeout(res, 0);
    });
  }
}

// LAS/LAZ file object
export class LASFile {
  private file: any;
  private numBytes: any;
  private version: any;
  private formatId: any;
  private loader: any;
  private isCompressed: boolean;
  private versionAsString: any;

  constructor(data) {
    const headerBytes = data.buffers[0];
    this.file = data.file;
    this.numBytes = data.numBytes;

    this.determineVersion(headerBytes);
    if (this.version > 14) {
      console.error("Las versions 1.1-1.4 are currently supported");
      return;
    }

    this.determineFormat(headerBytes);
    if (validDataFormats.indexOf(this.formatId) === -1) {
      console.error("The point format ID is not supported");
      return;
    }

    this.loader = new LASLoader(headerBytes, this.file, this.version, this.numBytes);
  }

  get maxNumberOfChunks() {
    return Math.ceil(this.numBytes / bytesPerChunk);
  }

  get needsMoreData() {
    let buffers = this.loader.arraybuffers;
    if (buffers.length === this.maxNumberOfChunks) {
      return false;
    }

    let activeBuffers = buffers.filter(x => x !== null);
    return activeBuffers.length <= 1;
  }

  get numPoints() {
    return this.loader.header.pointsCount;
  }

  get pointOffset() {
    return this.loader.header.pointsOffset;
  }

  get numberBuffers() {
    return this.loader.arraybuffers.length;
  }

  get pointsStructSize() {
    return this.loader.header.pointsStructSize;
  }


  determineFormat(headerBytes) {
    // Get format id from the first buffer
    let formatId = readAs(headerBytes, Uint8Array, 32 * 3 + 8);
    let bit7 = (formatId & 0x80) >> 7;
    let bit6 = (formatId & 0x40) >> 6;

    if (bit7 === 1 && bit6 === 1) {
      throw new Error("Old style compression not supported");
    }

    this.formatId = formatId & 0x3f;
    this.isCompressed = (bit7 === 1 || bit6 === 1);
  }

  determineVersion(headerBytes) {
    // Get version info from the first buffer
    let ver = new Int8Array(headerBytes, 24, 2);
    this.version = ver[0] * 10 + ver[1];
    this.versionAsString = ver[0] + "." + ver[1];
  }

  open() {
    return this.loader.open();
  }

  /** Return header with units forced to meters */
  getHeader(toMeters) {
    return this.loader.getHeader(toMeters);
  }

  readData() {
    return this.loader.readData();
  }

  getFileBuffers() {
    return new Promise((res, rej) => {
      if (!this.needsMoreData) {
        // No more additional data is required
        res();
        return;
      }

      let onSuccess = event => {
        event.buffers.forEach(buffer => {
          this.loader.arraybuffers.push(buffer);
        });

        console.log("Load more data chunks");
        res();
      };

      let dataLoaded = this.numberBuffers * bytesPerChunk;
      let startChunk = this.pointOffset + dataLoaded;

      if (this.loader.isRemoteFile) {
        // Load data (html5 file object)
        console.warn("Static las file loading not implemented");
        // parseFileChunks(this.file, startChunk, onSuccess);
      } else {
        // Load more data (node based)
        readBufferChunks(this.file.path, startChunk, bytesPerChunk, chunksToRead)
          .then(onSuccess);
      }
    });
  }

  close() {
    return this.loader.close();
  }
}
